import { useState } from "react";
import { Button, Icon, Input, Modal } from "@legal-500/legal500-design-system";
import { campaignsService, type Segment } from "@/api/campaigns.service";
import styles from "./MailingGroups.module.css";

type Props = {
  campaignId: string;
  segment: Segment;
  onClose: () => void;
  onScheduled: (segment: Segment) => void;
  onError: (message: string, status?: number) => void;
};

// The browser's IANA zone is sent as a scheduling hint (arch-discussion §14.2);
// it is not a constraint — the backend stores and schedules the absolute UTC.
const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Formats an absolute UTC ISO instant into the wall-clock value a
// <input type="datetime-local"> expects (YYYY-MM-DDTHH:mm, in local time), so a
// reschedule pre-fills the picker with the currently-scheduled moment.
function toDatetimeLocalValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

export function ScheduleSegmentModal({
  campaignId,
  segment,
  onClose,
  onScheduled,
  onError,
}: Props) {
  // Reschedule pre-fills the picker from the segment's current absolute time;
  // a fresh schedule starts empty (a future time must be picked before submit).
  const [scheduledAt, setScheduledAt] = useState(
    segment.scheduled_at ? toDatetimeLocalValue(segment.scheduled_at) : "",
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isReschedule = segment.status === "scheduled";
  const title = isReschedule ? "Reschedule send" : "Schedule send";

  // A schedule requires a concrete future instant: block an empty datetime and
  // any moment already in the past.
  const invalidEmpty = scheduledAt.trim() === "";
  const invalidPast = !invalidEmpty && new Date(scheduledAt).getTime() <= Date.now();

  const handleSubmit = async () => {
    if (invalidEmpty || invalidPast) return;
    setSubmitting(true);
    setError(null);

    try {
      const updated = await campaignsService.scheduleSegment(campaignId, segment.id, {
        scheduledAt: new Date(scheduledAt).toISOString(),
        timezone: BROWSER_TZ,
      });
      onScheduled(updated);
    } catch (err) {
      const status = (err as { status?: number }).status;
      const message = err instanceof Error ? err.message : "Failed to schedule send.";
      // 409 = illegal transition (e.g. already sending/sent): bubble up so the
      // parent shows a toast and closes; other errors stay inline for a retry.
      if (status === 409) {
        onError(message, status);
      } else {
        setError(message);
      }
    } finally {
      // Always clear the loading state so the button never stays stuck on
      // "Scheduling…" — including the 409 and generic-error paths.
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={title}
      size="medium"
      blurBackground
      backdropClick={false}
    >
      <div className={styles.modalForm}>
        <div className={styles.readonlyField}>
          <span className={styles.readonlyLabel}>Send</span>
          <span className={styles.readonlyValue}>{segment.label ?? segment.id}</span>
        </div>

        <div>
          <div className={styles.modalSectionLabel}>Schedule</div>
          <Input
            aria-label="Scheduled at"
            type="datetime-local"
            value={scheduledAt}
            onChange={e => setScheduledAt(e.target.value)}
            fullWidth
          />
          <p className={styles.tzHint}>
            Timezone hint: <code>{BROWSER_TZ}</code> — saved as absolute UTC.
          </p>
          {(invalidEmpty || invalidPast) && (
            <div className={styles.fieldError}>
              <Icon icon="exclamation" size="small" />
              <span>
                {invalidEmpty ? "Pick a date and time." : "Choose a date and time in the future."}
              </span>
            </div>
          )}
        </div>

        {error && (
          <div className={styles.fieldError}>
            <Icon icon="exclamation" size="small" />
            <span>{error}</span>
          </div>
        )}

        <div className={styles.modalFooter}>
          <Button variant="outlined" color="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={submitting || invalidEmpty || invalidPast}
          >
            {submitting ? "Scheduling…" : isReschedule ? "Reschedule" : "Schedule"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
